import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Palmtree,
  Heart,
  Calendar,
  Crown,
  Bot,
  ShieldCheck,
  LogOut,
  ChevronRight,
  MapPin,
  Star,
  User,
} from "lucide-react";
import { getSiteProfile } from "../lib/site-profile";
import { isProUser, patronTierLabel } from "../lib/patron";

const DashboardPage = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const siteProfile = getSiteProfile();
  const [favorites, setFavorites] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
      return;
    }
    if (user) {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [favResponse, bookResponse] = await Promise.all([
        axios.get(`${API}/users/favorites`, { withCredentials: true }),
        axios.get(`${API}/bookings`, { withCredentials: true })
      ]);
      setFavorites(favResponse.data?.favorites || []);
      setBookings(bookResponse.data?.bookings || bookResponse.data || []);
    } catch (error) {
      console.error("Fetch data error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="skeleton h-32 rounded-3xl mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-48 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Palmtree className="w-6 h-6 text-white" />
              </div>
              <span className="font-serif font-bold text-xl">{siteProfile.brandName}</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-white hover:bg-white/10">Explore</Button>
              </Link>
              <Button variant="ghost" className="text-white hover:bg-white/10" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* User Profile Section */}
      <div className="bg-primary pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-serif font-bold text-white">{user?.name}</h1>
              <p className="text-white/70">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={isProUser(user) ? "bg-secondary text-secondary-foreground" : "bg-white/20 text-white"}>
                  {isProUser(user) ? (
                    <><Crown className="w-3 h-3 mr-1" /> Explorer PRO</>
                  ) : (
                    "Free Plan"
                  )}
                </Badge>
              </div>
            </div>
            {!isProUser(user) && (
              <Link to="/premium" className="ml-auto">
                <Button className="btn-gold" data-testid="upgrade-btn">
                  <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 -mt-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link to="/bookings" className="bg-white rounded-2xl shadow-card p-6 hover:shadow-floating transition-all group" data-testid="bookings-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <p className="text-3xl font-bold mb-1">{bookings.length}</p>
            <p className="text-muted-foreground">Bookings</p>
          </Link>

          <div className="bg-white rounded-2xl shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-red-500" />
              </div>
            </div>
            <p className="text-3xl font-bold mb-1">{favorites.length}</p>
            <p className="text-muted-foreground">Favorites</p>
          </div>

          <div className="bg-white rounded-2xl shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center">
                <Crown className="w-6 h-6 text-secondary" />
              </div>
            </div>
            <p className="text-3xl font-bold mb-1">{patronTierLabel(user)}</p>
            <p className="text-muted-foreground">Subscription</p>
          </div>
        </div>

        {/* Upcoming Bookings */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-xl">Upcoming Bookings</h2>
            <Link to="/bookings" className="text-primary text-sm hover:underline">View All</Link>
          </div>
          {bookings.filter(b => b.status !== "cancelled").slice(0, 3).length === 0 ? (
            <div className="bg-white rounded-2xl shadow-card p-8 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No upcoming bookings</p>
              <Link to="/experiences">
                <Button data-testid="browse-btn">Browse Experiences</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.filter(b => b.status !== "cancelled").slice(0, 3).map((booking) => (
                <div key={booking.booking_id} className="bg-white rounded-2xl shadow-card p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">{booking.experience_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {booking.booking_date} at {booking.booking_time}
                    </p>
                  </div>
                  <Badge className={booking.status === "confirmed" ? "bg-accent" : "bg-secondary"}>
                    {booking.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Favorites */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-xl">Favorite Spots</h2>
          </div>
          {favorites.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-card p-8 text-center">
              <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No favorites yet</p>
              <Link to="/discover">
                <Button data-testid="discover-btn">Discover Spots</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {favorites.slice(0, 6).map((spot) => (
                <Link 
                  key={spot.spot_id} 
                  to={`/spot/${spot.spot_id}`}
                  className="bg-white rounded-2xl shadow-card overflow-hidden group"
                >
                  <div className="relative h-32 overflow-hidden">
                    <img
                      src={spot.images?.[0] || "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400"}
                      alt={spot.name}
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold group-hover:text-primary transition-colors">{spot.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-3 h-3" />
                      <span className="capitalize">{spot.country}</span>
                      <Star className="w-3 h-3 text-secondary fill-secondary ml-2" />
                      <span>{spot.rating?.toFixed(1)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Quick Links */}
        <section className="mb-8">
          <h2 className="font-serif font-bold text-xl mb-4">Quick Links</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/discover" className="bg-white rounded-2xl shadow-card p-4 text-center hover:shadow-floating transition-all">
              <Palmtree className="w-8 h-8 text-primary mx-auto mb-2" />
              <span className="font-medium">Discover</span>
            </Link>
            <Link to="/experiences" className="bg-white rounded-2xl shadow-card p-4 text-center hover:shadow-floating transition-all">
              <Calendar className="w-8 h-8 text-primary mx-auto mb-2" />
              <span className="font-medium">Experiences</span>
            </Link>
            <Link to="/premium" className="bg-white rounded-2xl shadow-card p-4 text-center hover:shadow-floating transition-all">
              <Crown className="w-8 h-8 text-secondary mx-auto mb-2" />
              <span className="font-medium">Premium</span>
            </Link>
            <Link to="/assistant" className="bg-white rounded-2xl shadow-card p-4 text-center hover:shadow-floating transition-all">
              <Bot className="w-8 h-8 text-primary mx-auto mb-2" />
              <span className="font-medium">Assistant</span>
            </Link>
            <Link to="/admin" className="bg-white rounded-2xl shadow-card p-4 text-center hover:shadow-floating transition-all">
              <ShieldCheck className="w-8 h-8 text-primary mx-auto mb-2" />
              <span className="font-medium">Admin</span>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default DashboardPage;
